import { useDispatch, useSelector } from 'react-redux';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useConfirmModal from '../useConfirmModal';
import { RootState } from '../../store/store';
import {
    closeAddProductModal,
    closeEditProductModal,
} from '../../store/slices/sale/creditFiscalSaleSlice';
import {
    setSaleProducts,
    updateSaleTotals,
    updateSaleTotalsView
} from '../../store/slices/sale/saleSlice';
import { useSale } from './useSale';
import CreditFiscalSaleService from '../../services/sale/CreditFiscalSaleService';
import useApiErrorHandler from '../useApiErrorHandler';

export const useCreditFiscalSale = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const confirm = useConfirmModal();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerSelectProduct,
    } = useSale();

    const { showAddProductModal, showEditProductModal } = useSelector(
        (state: RootState) => state.creditFiscalSale
    );

    const handlerAddCreditFiscalProduct = async (product: any) => {
        // console.log(product);
        try {
            showLoadingIndicator();
            const response = await CreditFiscalSaleService.addProduct(product);
            if (response.statusCode === 201) {
                const { title, message, type, data } = response;
                const { product: savedProduct, products, lines, totals, totalsView } = data;
                const recalculatedProducts = products ?? lines;

                if (recalculatedProducts) dispatch(setSaleProducts(recalculatedProducts));
                else handlerAddProduct(savedProduct);
                dispatch(updateSaleTotals(totals)); // nombre genérico y reutilizable
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(closeAddProductModal());
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo agregar el producto.');
            }
        } catch (error: any) {
            handle(error); // Manejo centralizado de errores
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerUpdateCreditFiscalProduct = async (product: any) => {
        console.log(product)
        try {
            showLoadingIndicator();
            const response = await CreditFiscalSaleService.updateProduct(product);
            console.log(response);

            if (response.statusCode === 200) {
                const { title, message, type, data } = response;
                const { product: updatedProduct, products, lines, totals, totalsView } = data;
                const recalculatedProducts = products ?? lines;

                if (recalculatedProducts) dispatch(setSaleProducts(recalculatedProducts));
                else handlerUpdateProduct(updatedProduct);
                dispatch(updateSaleTotals(totals));   // unificado para todos los documentos
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(closeEditProductModal());
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el producto.');
            }
        } catch (error: any) {
            handle(error); // Manejo centralizado de errores
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerDeleteCreditFiscalProduct = async (id: number) => {
        confirm({
            title: '¿Eliminar Producto?',
            message: '¿Estás seguro que deseas eliminar el producto?',
            icon: 'warning',
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            confirmArgs: id,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await CreditFiscalSaleService.deleteProduct(id);

                    if (response.statusCode === 200) {
                        const { title, message, type, data } = response;

                        if (data?.products ?? data?.lines) {
                            dispatch(setSaleProducts(data.products ?? data.lines));
                        }
                        else handlerDeleteProduct(id);
                        dispatch(updateSaleTotals(data?.totals)); // totales unificados
                        dispatch(updateSaleTotalsView(data?.totalsView));
                        showAlert(type, title, message);
                    } else {
                        showAlert('error', 'Error', 'No se pudo eliminar el producto.');
                    }
                } catch (error: any) {
                    handle(error); // Manejo centralizado de errores
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };


    const handlerGetProductById = async (detailId: number) => {
        try {
            showLoadingIndicator();
            const response = await CreditFiscalSaleService.getProductById(detailId);
            // console.log(response);

            if (response.statusCode === 200) {
                handlerSelectProduct(response.data)
                return response.data;
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el producto.');
            }
        } catch (error: any) {
            handle(error); // Manejo centralizado de errores
        } finally {
            hideLoadingIndicator();
        }
    };

    return {
        handlerAddCreditFiscalProduct,
        handlerUpdateCreditFiscalProduct,
        handlerDeleteCreditFiscalProduct,
        handlerGetProductById,
        showAddProductModal,
        showEditProductModal,
    };
};
